import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";
import useHelpers from "composables/useHelpers";
import { shouldFetch } from "common/fetchHelpers.js";

/**
 * Learning Objectives Store
 *
 * This store manages the state and actions related to the learning objectives.
 */
export const useGlobalSchoolDataStore = defineStore("globalSchoolData", () => {
  const mentorClasses = ref([]);
  const educationLevels = ref([]);
  const allEducationLevels = ref([]);
  const subjects = ref([]);
  const lessonGroups = ref([]);
  const learningObjectivesLessongroups = ref([]);
  const currentTeacherLessonGroups = ref([]);
  const interdisciplinarySubjects = ref([]);
  const generalMeetingData = ref({});
  const publicTags = ref([]);
  const publicLobCompetences = ref([]);
  const publicSkills = ref([]);
  const publicSkillCategories = ref([]);
  const publicSubjects = ref([]);
  const publicEducationLevels = ref([]);
  const portfolioSources = ref([]);

  // States
  /**
   * State indicating if data is being fetched.
   * @type {import('vue').Ref<Object>}
   */
  const isFetching = ref({
    mentor_classes: false,
    education_levels: false,
    all_education_levels: false,
    subjects: false,
    lessongroups: false,
    learningObjectivesLessongroups: false,
    currentTeacherLessonGroups: false,
    interdisciplinary_subjects: false,
    general_meeting_data: false,
    public_tags: false,
    public_lob_competences: false,
    public_skills: false,
    public_skill_categories: false,
    public_subjects: false,
    public_education_levels: false,
    portfolio_sources: false,
  });

  /**
   * State indicating if data has been fetched.
   * @type {import('vue').Ref<Object>}
   */
  const hasFetched = ref({
    mentor_classes: false,
    education_levels: false,
    all_education_levels: false,
    subjects: false,
    lessongroups: false,
    learningObjectivesLessongroups: false,
    currentTeacherLessonGroups: false,
    interdisciplinary_subjects: false,
    general_meeting_data: false,
    public_tags: false,
    public_lob_competences: false,
    public_skills: false,
    public_skill_categories: false,
    public_subjects: false,
    public_education_levels: false,
    portfolio_sources: false,
  });

  const hasNoMeetingsBanner = (meetingType) => {
    const { mapMeetingType } = useHelpers();

    let meetingWebinar;
    switch (meetingType) {
      case "meetings":
        meetingWebinar = "https://www.youtube.com/watch?v=YNgUuhZ2fw8";
        break;
      case "transition_meetings":
        meetingWebinar = "https://www.youtube.com/watch?v=7Wx3ygBufAo";
        break;
      case "group_meetings":
        meetingWebinar = "https://www.youtube.com/watch?v=5rK5FKBwlSY";
    }

    return {
      enabled: generalMeetingData.value?.[meetingType] === 0,
      message: `We zien dat dit de eerste keer is dat je een ${mapMeetingType(meetingType).toLowerCase()} organiseert. Goed bezig! Hulp en tips nodig? Neem contact op met onze onderwijsadviseur. We kunnen jullie bijvoorbeeld via een korte online afspraak op weg helpen.`,
      webinar: meetingWebinar || undefined,
    };
  };

  /**
   * Fetches mentor classes.
   * @param {Object} options - The options for fetching mentor classes.
   * @param {boolean} [options.forceFetch=false] - Whether to force fetch the data.
   * @returns {Promise<Array<Object>>} The fetched mentor classes.
   */
  const fetchMentorClasses = async ({ forceFetch = false } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["mentor_classes"]) return;

    if (!shouldFetch(hasFetched.value["mentor_classes"]) && !forceFetch) {
      return mentorClasses.value || [];
    }

    try {
      isFetching.value["mentor_classes"] = true;

      const fetchedClasses = await axios.get(
        `/api/school/${schoolId}/mentor-classes?array=true`,
      );

      hasFetched.value["mentor_classes"] = new Date();
      mentorClasses.value = fetchedClasses.data;

      return mentorClasses.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["mentor_classes"] = false;
    }
  };

  /**
   * Fetches education levels.
   * @param {Object} options - The options for fetching education levels.
   * @param {boolean} [options.forceFetch=false] - Whether to force fetch the data.
   * @returns {Promise<Array<Object>>} The fetched education levels.
   */
  const fetchEducationLevels = async ({ forceFetch = false } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["education_levels"]) return;

    if (!shouldFetch(hasFetched.value["education_levels"]) && !forceFetch) {
      return educationLevels.value || [];
    }

    try {
      isFetching.value["education_levels"] = true;

      const fetchedLevels = await axios.get(
        `/api/school/${schoolId}/education-levels`,
      );

      hasFetched.value["education_levels"] = new Date();
      educationLevels.value = fetchedLevels.data;

      return educationLevels.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["education_levels"] = false;
    }
  };

  const fetchAllSchoolEducationLevels = async ({ forceFetch = false } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["all_education_levels"]) return;

    if (!shouldFetch(hasFetched.value["all_education_levels"]) && !forceFetch) {
      return allEducationLevels.value || [];
    }

    try {
      isFetching.value["all_education_levels"] = true;

      const fetchedLevels = await axios.get(
        `/api/school/${schoolId}/all-education-levels`,
      );

      hasFetched.value["all_education_levels"] = new Date();
      allEducationLevels.value = fetchedLevels.data;

      return allEducationLevels.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["all_education_levels"] = false;
    }
  };

  /**
   * Fetches interdisciplinary subjects.
   * @param {Object} options - The options for fetching interdisciplinary subjects.
   * @param {boolean} [options.forceFetch=false] - Whether to force fetch the data.
   * @returns {Promise<Array<Object>>} The fetched interdisciplinary subjects.
   */
  const fetchInterdisciplinarySubjects = async ({
    forceFetch = false,
  } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["interdisciplinary_subjects"]) return;

    if (
      !shouldFetch(hasFetched.value["interdisciplinary_subjects"]) &&
      !forceFetch
    ) {
      return interdisciplinarySubjects.value || [];
    }

    try {
      isFetching.value["interdisciplinary_subjects"] = true;

      const fetchedSubjects = await axios.get(
        `/api/school/${schoolId}/subjects-with-interdisciplinary`,
      );

      hasFetched.value["interdisciplinary_subjects"] = new Date();
      interdisciplinarySubjects.value = fetchedSubjects.data;

      return interdisciplinarySubjects.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["interdisciplinary_subjects"] = false;
    }
  };

  /**
   * Fetches subjects.
   * @param {Object} options - The options for fetching subjects.
   * @param {boolean} [options.forceFetch=false] - Whether to force fetch the data.
   * @returns {Promise<Array<Object>>} The fetched subjects.
   */
  const fetchSubjects = async ({ forceFetch = false } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["subjects"]) return;

    if (!shouldFetch(hasFetched.value["subjects"]) && !forceFetch) {
      return subjects.value || [];
    }

    try {
      isFetching.value["subjects"] = true;

      const { data: fetchedSubjects } = await axios.get(
        `/api/school/${schoolId}/subjects`,
      );

      hasFetched.value["subjects"] = new Date();
      subjects.value = fetchedSubjects.data;

      return subjects.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["subjects"] = false;
    }
  };

  /**
   * Fetches lesson groups.
   * @param {Object} options - The options for fetching lesson groups.
   * @param {boolean} [options.forceFetch=false] - Whether to force fetch the data.
   * @returns {Promise<Array<Object>>} The fetched lesson groups.
   */
  const fetchLessonGroups = async ({ forceFetch = false } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["lessongroups"]) return;

    if (!shouldFetch(hasFetched.value["lessongroups"]) && !forceFetch) {
      return lessonGroups.value || [];
    }

    try {
      isFetching.value["lessongroups"] = true;

      const { data: fetchedLessonGroups } = await axios.get(
        `/api/school/${schoolId}/lesson-groups`,
      );

      hasFetched.value["lessongroups"] = new Date();
      lessonGroups.value = fetchedLessonGroups.data;

      return lessonGroups.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["lessongroups"] = false;
    }
  };

  const fetchLearningObjectivesLessongroups = async ({
    forceFetch = false,
  } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["learningObjectivesLessongroups"]) {
      return;
    }

    if (
      !shouldFetch(hasFetched.value["learningObjectivesLessongroups"]) &&
      !forceFetch
    ) {
      return learningObjectivesLessongroups.value || [];
    }

    try {
      isFetching.value["learningObjectivesLessongroups"] = true;

      const { data: fetchedLessongroups } = await axios.get(
        `/api/school/${schoolId}/learning-goals/lesson-groups`,
      );

      hasFetched.value["learningObjectivesLessongroups"] = new Date();

      learningObjectivesLessongroups.value = fetchedLessongroups.data;

      return learningObjectivesLessongroups.value;
    } catch (error) {
      console.log("🚀 ~ useGlobalSchoolDataStore ~ error:", error);

      throw new Error("Error fetching lesson groups");
    } finally {
      isFetching.value["learningObjectivesLessongroups"] = false;
    }
  };

  const fetchCurrentTeacherLessonGroups = async ({
    forceFetch = false,
  } = {}) => {
    if (isFetching.value["currentTeacherLessonGroups"]) {
      return isFetching.value["currentTeacherLessonGroups"];
    }

    const { employeeId } = useHelpers();

    if (
      (!shouldFetch(hasFetched.value["currentTeacherLessonGroups"]) &&
        !forceFetch) ||
      !employeeId
    ) {
      return currentTeacherLessonGroups.value || [];
    }

    isFetching.value["currentTeacherLessonGroups"] = (async () => {
      try {
        const {
          data: { items: fetchedLessonGroups },
        } = await axios.get(`/api/teacher/${employeeId}/lessongroups`);

        hasFetched.value["currentTeacherLessonGroups"] = new Date();

        currentTeacherLessonGroups.value = fetchedLessonGroups.map(
          ({ lesson_class, subject }) => ({
            lesson_class,
            name: lesson_class,
            subject,
          }),
        );

        return currentTeacherLessonGroups.value;
      } catch (error) {
        console.log(
          "🚀 ~ isFetching.value['currentTeacherLessonGroups']= ~ error:",
          error,
        );

        if (axios.isAxiosError(error)) {
          return Promise.reject(error.response);
        }
      } finally {
        isFetching.value["currentTeacherLessonGroups"] = false;
      }
    })();

    return isFetching.value["currentTeacherLessonGroups"];
  };

  const fetchGeneralMeetingData = async ({ forceFetch = false } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching.value["general_meeting_data"]) return;

    if (!shouldFetch(hasFetched.value["general_meeting_data"]) && !forceFetch) {
      return generalMeetingData.value || [];
    }

    try {
      isFetching.value["general_meeting_data"] = true;

      const { data: fetchedInfo } = await axios.get(
        `/api/schools/${schoolId}/meeting-types-counter`,
      );

      hasFetched.value["general_meeting_data"] = new Date();
      generalMeetingData.value = fetchedInfo;

      return subjects.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["general_meeting_data"] = false;
    }
  };

  const fetchPublicTags = async ({ forceFetch = false } = {}) => {
    if (isFetching.value["public_tags"]) return;

    if (!shouldFetch(hasFetched.value["public_tags"]) && !forceFetch) {
      return publicTags.value || [];
    }

    try {
      isFetching.value["public_tags"] = true;

      const { data: fetchedTags } = await axios.get(
        `/api/public-portfolio-tags`,
      );

      hasFetched.value["public_tags"] = new Date();
      publicTags.value = fetchedTags.data;

      return publicTags.value;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error);
        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["public_tags"] = false;
    }
  };

  const fetchPublicLobCompetences = async ({ forceFetch = false } = {}) => {
    if (isFetching.value["public_lob_competences"]) return;

    if (
      !shouldFetch(hasFetched.value["public_lob_competences"]) &&
      !forceFetch
    ) {
      return publicLobCompetences.value || [];
    }

    try {
      isFetching.value["public_lob_competences"] = true;

      const { data: fetchedCompetences } = await axios.get(
        `/api/public-portfolio-lob-competences`,
      );

      hasFetched.value["public_lob_competences"] = new Date();
      publicLobCompetences.value = fetchedCompetences.data;

      return publicLobCompetences.value;
    } catch (e) {
      throw new Error(e);
    } finally {
      isFetching.value["public_lob_competences"] = false;
    }
  };

  const fetchPublicSkills = async ({ forceFetch = false } = {}) => {
    if (isFetching.value["public_skills"]) return;

    if (!shouldFetch(hasFetched.value["public_skills"]) && !forceFetch) {
      return publicSkills.value || [];
    }

    try {
      isFetching.value["public_skills"] = true;

      const { data: fetchedSkills } = await axios.get(
        `/api/public-portfolio-skills`,
      );

      hasFetched.value["public_skills"] = new Date();
      publicSkills.value = fetchedSkills.data;

      return publicSkills.value;
    } catch (e) {
      throw new Error(e);
    } finally {
      isFetching.value["public_skills"] = false;
    }
  };

  const fetchPublicSkillCategories = async ({ forceFetch = false } = {}) => {
    if (isFetching.value["public_skill_categories"]) return;

    if (
      !shouldFetch(hasFetched.value["public_skill_categories"]) &&
      !forceFetch
    ) {
      return publicSkillCategories.value || [];
    }

    try {
      isFetching.value["public_skill_categories"] = true;

      const { data: fetchedCategories } = await axios.get(
        `/api/public-portfolio-skill-categories`,
      );

      hasFetched.value["public_skill_categories"] = new Date();
      publicSkillCategories.value = fetchedCategories.data;

      return publicSkillCategories.value;
    } catch (e) {
      throw new Error(e);
    } finally {
      isFetching.value["public_skill_categories"] = false;
    }
  };

  const fetchPublicSubjects = async ({ forceFetch = false } = {}) => {
    if (isFetching.value["public_subjects"]) return;

    if (!shouldFetch(hasFetched.value["public_subjects"]) && !forceFetch) {
      return publicSubjects.value || [];
    }

    try {
      isFetching.value["public_subjects"] = true;

      const { data: fetchedSubjects } = await axios.get(
        `/api/public-portfolio-subjects`,
      );

      hasFetched.value["public_subjects"] = new Date();
      publicSubjects.value = fetchedSubjects;

      return publicSubjects.value;
    } catch (e) {
      throw new Error(e);
    } finally {
      isFetching.value["public_subjects"] = false;
    }
  };

  const fetchPublicEducationLevels = async ({ forceFetch = false } = {}) => {
    if (isFetching.value["public_education_levels"]) return;

    if (
      !shouldFetch(hasFetched.value["public_education_levels"]) &&
      !forceFetch
    ) {
      return publicEducationLevels.value || [];
    }

    try {
      isFetching.value["public_education_levels"] = true;

      const { data: fetchedLevels } = await axios.get(
        `/api/public-portfolio-education-levels`,
      );

      hasFetched.value["public_education_levels"] = new Date();
      publicEducationLevels.value = fetchedLevels;

      return publicEducationLevels.value;
    } catch (e) {
      throw new Error(e);
    } finally {
      isFetching.value["public_education_levels"] = false;
    }
  };

  const fetchPortfolioSources = async ({ forceFetch = false } = {}) => {
    if (isFetching.value["portfolio_sources"]) return;

    if (!shouldFetch(hasFetched.value["portfolio_sources"]) && !forceFetch) {
      return portfolioSources.value || [];
    }

    try {
      isFetching.value["portfolio_sources"] = true;

      const { data: fetchedSources } = await axios.get(
        `/api/portfolio-sources`,
      );

      hasFetched.value["portfolio_sources"] = new Date();
      portfolioSources.value = fetchedSources.data;

      return portfolioSources.value;
    } catch (e) {
      throw new Error(e);
    } finally {
      isFetching.value["portfolio_sources"] = false;
    }
  };

  return {
    mentorClasses,
    allEducationLevels,
    educationLevels,
    subjects,
    lessonGroups,
    learningObjectivesLessongroups,
    currentTeacherLessonGroups,
    interdisciplinarySubjects,
    generalMeetingData,
    publicTags,
    publicLobCompetences,
    publicSkills,
    publicSkillCategories,
    publicSubjects,
    publicEducationLevels,
    portfolioSources,
    hasNoMeetingsBanner,
    fetchMentorClasses,
    fetchEducationLevels,
    fetchAllSchoolEducationLevels,
    fetchSubjects,
    fetchLessonGroups,
    fetchLearningObjectivesLessongroups,
    fetchCurrentTeacherLessonGroups,
    fetchInterdisciplinarySubjects,
    fetchGeneralMeetingData,
    fetchPublicTags,
    fetchPublicLobCompetences,
    fetchPublicSkills,
    fetchPublicSkillCategories,
    fetchPublicSubjects,
    fetchPublicEducationLevels,
    fetchPortfolioSources,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useGlobalSchoolDataStore, import.meta.hot),
  );
}
